import { motion } from "framer-motion";
import { Users, Repeat, Sparkles, Truck, MessageCircle, ChevronRight } from "lucide-react";
import { useSettings, buildWhatsAppLink } from "@/context/SettingsContext";

function TrustBadge({ Icon, value, label, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 + idx * 0.08 }}
      className="flex items-start gap-3 p-4 border border-[#2A2A2A] bg-[#0F0F0F]/60 backdrop-blur-sm"
      data-testid={`hero-trust-badge-${idx}`}
    >
      <div className="w-9 h-9 shrink-0 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37]">
        <Icon className="w-4 h-4" />
      </div>
      <div className="leading-tight">
        {value && (
          <div className="font-serif text-xl text-white" data-testid={`hero-trust-value-${idx}`}>
            {value}
          </div>
        )}
        <div className="text-[0.72rem] uppercase tracking-[0.18em] text-white/65">
          {label}
        </div>
      </div>
    </motion.div>
  );
}

export default function Hero() {
  const { settings } = useSettings();
  const waLink = buildWhatsAppLink(settings);
  const s = settings || {};

  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden grain" data-testid="hero-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-[480px] h-[480px] rounded-full bg-[#D4AF37]/8 blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#D4AF37]/5 blur-[140px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            {s.hero_eyebrow && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D4AF37]/40 mb-8"
                data-testid="hero-eyebrow"
              >
                <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-pulse" />
                <span className="text-[0.65rem] tracking-[0.28em] uppercase text-[#D4AF37]">
                  {s.hero_eyebrow}
                </span>
              </motion.div>
            )}

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight text-white"
              data-testid="hero-headline"
            >
              {s.hero_headline}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-6 text-lg md:text-xl text-white/70 max-w-xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              {s.hero_subheadline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <a
                href={s.hero_cta_browse_link || "#collections"}
                className="btn-primary justify-center"
                data-testid="hero-browse-collections-button"
              >
                {s.hero_cta_browse_text || "Browse Collections"}
                <ChevronRight className="w-4 h-4" />
              </a>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="btn-whatsapp justify-center"
                data-testid="hero-whatsapp-button"
              >
                <MessageCircle className="w-5 h-5" />
                {s.hero_cta_whatsapp_text || "Chat on WhatsApp"}
              </a>
            </motion.div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <TrustBadge idx={0} Icon={Users} value={s.stat_customers} label={s.stat_customers_label} />
              <TrustBadge idx={1} Icon={Repeat} value={s.stat_repeat} label={s.stat_repeat_label} />
              <TrustBadge idx={2} Icon={Sparkles} label={s.stat_premium_label} />
              <TrustBadge idx={3} Icon={Truck} label={s.stat_delivery_label} />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-5"
            data-testid="hero-visual"
          >
            <div className="relative aspect-[4/5] placeholder-frame overflow-hidden">
              {s.hero_image ? (
                <img
                  src={s.hero_image}
                  alt={s.brand_name || "Luxury Hair by DEMJ"}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="hero-image"
                />
              ) : (
                <>
                  <span className="placeholder-tag">Hero Product Photo</span>
                  <div className="placeholder-center">
                    <div className="font-serif text-[#D4AF37] text-2xl">DEMJ</div>
                    <div className="text-xs tracking-[0.32em] text-white/55 uppercase">
                      Premium Human Hair
                    </div>
                    <div className="mt-6 w-12 h-px bg-[#D4AF37]/60" />
                    <div className="text-[0.68rem] text-white/40 mt-3 max-w-[210px]">
                      Upload a hero image from the admin panel to replace this placeholder.
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
